import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminAPI, logout } from '../lib/adminAPI';
import { ArrowLeft, Mail, Phone, MapPin, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { LeadStatusSelect } from '../components/admin/LeadStatus';

const ContactDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContact();
  }, [id]);

  const loadContact = async () => {
    try {
      const data = await adminAPI.getContactById(id);
      setContact(data);
    } catch (error) {
      if (error.response?.status === 401) {
        logout();
      }
      toast.error('Failed to load contact details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading contact details...</div>;
  }

  if (!contact) {
    return <div className="admin-error">Contact not found</div>;
  }

  return (
    <div className="admin-page">
      <div className="admin-header">
        <div>
          <button onClick={() => navigate('/admin/contacts')} className="admin-back-btn">
            <ArrowLeft size={20} />
            Back to Contacts
          </button>
          <h1 className="admin-page-title">Contact Details</h1>
        </div>
        <div className="admin-header-actions">
          <div className="lead-status-inline" data-testid="contact-detail-status">
            <span className="lead-status-inline-label">Status</span>
            <LeadStatusSelect
              contactId={contact.id}
              status={contact.status}
              size="lg"
              onChange={(_, status) => setContact(prev => ({ ...prev, status }))}
            />
          </div>
          <button onClick={logout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </div>

      <div className="admin-detail-container">
        <div className="admin-detail-card">
          <h2 className="admin-detail-section-title">Contact Information</h2>
          <div className="admin-detail-grid">
            <div className="admin-detail-item">
              <label>Full Name</label>
              <p>{contact.fullName}</p>
            </div>
            <div className="admin-detail-item">
              <label><Mail size={16} /> Email</label>
              <p><a href={`mailto:${contact.email}`}>{contact.email}</a></p>
            </div>
            <div className="admin-detail-item">
              <label><Phone size={16} /> Mobile</label>
              <p><a href={`tel:${contact.mobile}`}>{contact.mobile}</a></p>
            </div>
            <div className="admin-detail-item">
              <label><Calendar size={16} /> Submitted</label>
              <p>{new Date(contact.submittedAt).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="admin-detail-card">
          <h2 className="admin-detail-section-title">Property Details</h2>
          <div className="admin-detail-grid">
            {contact.source === 'google_ads_lead_form' && (
              <div className="admin-detail-item">
                <label>Source</label>
                <p>Google Ads lead form{contact.isTest ? ' (test lead)' : ''}{contact.googleCampaignId ? ` · Campaign ${contact.googleCampaignId}` : ''}</p>
              </div>
            )}
            {contact.source === 'chatbot' && (
              <div className="admin-detail-item">
                <label>Source</label>
                <p>Website chat (Mia){contact.landingPage ? ` · from ${contact.landingPage}` : ''}</p>
              </div>
            )}
            <div className="admin-detail-item">
              <label><MapPin size={16} /> Property Address</label>
              <p>{contact.propertyAddress}</p>
            </div>
            {contact.suburb && (
              <div className="admin-detail-item">
                <label>Suburb</label>
                <p>{contact.suburb}</p>
              </div>
            )}
            {contact.postcode && (
              <div className="admin-detail-item">
                <label>Postcode</label>
                <p>{contact.postcode}</p>
              </div>
            )}
            {contact.propertyType && (
              <div className="admin-detail-item">
                <label>Property Type</label>
                <p>{contact.propertyType}</p>
              </div>
            )}
            {contact.timeframe && (
              <div className="admin-detail-item">
                <label>Timeframe</label>
                <p>{contact.timeframe}</p>
              </div>
            )}
          </div>
        </div>

        {contact.lookingFor && contact.lookingFor.length > 0 && (
          <div className="admin-detail-card">
            <h2 className="admin-detail-section-title">Service Requirements</h2>
            <div className="admin-tags-large">
              {contact.lookingFor.map((item, idx) => (
                <span key={idx} className="admin-tag-large">{item}</span>
              ))}
            </div>
          </div>
        )}

        {contact.message && (
          <div className="admin-detail-card">
            <h2 className="admin-detail-section-title">Message</h2>
            <p className="admin-detail-message">{contact.message}</p>
          </div>
        )}

        {contact.recaptchaScore && (
          <div className="admin-detail-card">
            <h2 className="admin-detail-section-title">Security</h2>
            <div className="admin-detail-item">
              <label>reCAPTCHA Score</label>
              <p>
                <span className={`admin-score-badge ${contact.recaptchaScore >= 0.7 ? 'score-high' : contact.recaptchaScore >= 0.5 ? 'score-medium' : 'score-low'}`}>
                  {contact.recaptchaScore.toFixed(2)}
                </span>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactDetail;
